import React, { FC } from "react";
import AllRoles from "./roles";
import * as Router from "./polyrouter";
import * as Roles from "./rbac";
import { Requirement } from "./rbac";
import AppRoute from "./routes/AppRoute";
import { DropdownButton } from "./elements";

/**
 * So we have a roles framework that wants a bunch of requirement functions
 * And we know all the role strings the service uses from ./roles
 * And we want to be able to use the flexibility of the requirements, with simplicity of string specification.
 * This file sets up common aliases for various foundational elements that allow you to safely use the
 * permission name the server recognizes with the flexibility of the requirement methods from rbac
 */

// Define our basic need that takes a single role name.
export const needs = (n: AllRoles) => Roles.needs(n);
// Setup the guards that operate on a single parameter
export const useGuard = (n: AllRoles) => Roles.useGuard(needs(n));
export const protect = (n: AllRoles) => Roles.protect(needs(n));
//All our wrappers will either take a role name string, or the flexible requirement method.
type Requires = {
  requirement: AllRoles | Requirement;
  redirectUrl?: string;
};
export const Guard: FC<Requires> = ({ requirement, children, redirectUrl }) => {
  const realReq =
    typeof requirement === "string" ? needs(requirement) : requirement;
  return (
    <Roles.Guard
      requirement={realReq}
      reqName={requirement}
      redirectUrl={redirectUrl}
    >
      {children}
    </Roles.Guard>
  );
};

function withGuard<T extends object>(
  Component: React.ComponentType<T>
): React.ComponentType<
  T & {
    requirement: AllRoles | Requirement;
    disabled?: boolean;
    redirectUrl?: string;
  }
> {
  return ({
    requirement,
    redirectUrl,
    ...props
  }: T & { requirement: AllRoles | Requirement; redirectUrl?: string }) => (
    <Guard requirement={requirement} redirectUrl={redirectUrl}>
      {/* We know that our props in are T
          and requirement to a component that needs just T
          where we already removed requirements, this cast is safe. */}
      <Component {...(props as T)} />
    </Guard>
  );
}

//Routing Permission components
export const ProtectedLink = withGuard(Router.Link);
export const ProtectedNavLink = withGuard(Router.NavLink);
export const ProtectedRoute = withGuard(Router.Route);
export const ProtectedAppRoute = withGuard(AppRoute);
/*
 * There is no switch here, switch is a special component and it does read its children.
 * You don't strictly need switch to understand roles, as route will and if the route is not permitted it will render nothing
 * You should not end up on a forbidden route normally, as the links/buttons themselves to that component
 * should also be setup with permissions
 */

// withGuard expects a React.ComponentType, so this is turning a basic button element into a React Component so withGuard can accept it
const BasicButton: React.FC<React.ButtonHTMLAttributes<
  HTMLButtonElement
>> = props => {
  return <button {...props}>{props.children}</button>;
};

export const ProtectedButton = withGuard(BasicButton);
export const ProtectedDropdownButton = withGuard(DropdownButton);
